import Component from '@glimmer/component'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import type AnalyticsService from 'teacher-ui-client/services/analytics'
import type TeacherEvents from 'teacher-ui-client/services/teacher-events'
import type { IntlService } from 'ember-intl'
import { service } from '@ember/service'
import { type PrintablesLibraryBookEntry } from 'teacher-ui-client/contentful-client/entry-types'
import type { PDFType } from 'teacher-ui-client/components/printables/pdf-types'
import { PDF_TYPES } from 'teacher-ui-client/components/printables/pdf-types'
import { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import type { SubscriptionTypeService } from 'teacher-ui-client/services/subscription-type'
import { on } from '@ember/modifier'
import PopoverStandardDropdown from '@blakeelearning/popovers/components/popover/dropdowns/standard'
import { fn } from '@ember/helper'
import { ActionMenu } from 'teacher-ui-client/components/base/action-menu'
import { ActionMenuItem } from 'teacher-ui-client/components/base/action-menu/item'
import { sendPdfDownloadEvent } from 'teacher-ui-client/modifiers/pdf-download-event'

import { t } from 'ember-intl'
import { eq } from 'ember-truth-helpers'

interface Signature {
  Element: HTMLButtonElement
  Args: {
    activeBook: PrintablesLibraryBookEntry['fields']
    subscriptionType?: SubscriptionType
  }
}

interface OptionItem {
  title: string
  url: string
  fileName: string
  pdfType: PDFType
  pdfTypeFormatted: string
}

export class BookResourceOptions extends Component<Signature> {
  @service analytics!: AnalyticsService
  @service teacherEvents!: TeacherEvents
  @service intl!: IntlService
  @service subscriptionType!: SubscriptionTypeService

  get availablePdfOptions(): OptionItem[] {
    const bookTypes = PDF_TYPES
    return bookTypes
      .filter((pdfType) => this.args.activeBook[pdfType])
      .map((pdfType) => {
        const pdfFields = this.args.activeBook[pdfType]?.fields
        return {
          title: pdfFields?.title ?? '',
          url: pdfFields?.file?.url ?? '',
          fileName: pdfFields?.file?.fileName ?? '',
          pdfType,
          pdfTypeFormatted: this.intl.t(`printables.mediaType.${pdfType}`),
        }
      })
  }

  onOptionClick = (optionItem: OptionItem) => {
    sendPdfDownloadEvent(
      { analytics: this.analytics, teacherEvents: this.teacherEvents, subscriptionType: this.subscriptionType },
      {
        category: 'Printables Library',
        label: optionItem.title,
        file: optionItem.fileName,
        pdfType: optionItem.pdfType,
        via: 'dropdown',
      },
      this.args.subscriptionType,
    )
    window.open(optionItem.url)
  }

  <template>
    <PopoverStandardDropdown as |dropdown _status actions|>
      <button
        data-test-download-button
        ...attributes
        aria-haspopup="menu"
        {{dropdown.makeTrigger}}
        type="button"
        class="grid grid-cols-[1fr_auto_1fr] items-center rounded-md px-5 py-2 text-center font-semibold text-white select-none
          {{if (eq @subscriptionType SubscriptionType.Reading) 'bg-oceany-blue-350'}}
          {{if (eq @subscriptionType SubscriptionType.Maths) 'bg-ms-green-350'}}
          {{if (eq @subscriptionType SubscriptionType.Writing) 'bg-wl-blue-300'}}"
      >
        <div></div>
        <span>{{t "download"}}</span>
        <PhosphorIcon @icon="phosphor-caret-down-bold" class="h-3.5 w-3.5 justify-self-end" />
      </button>
      <dropdown.content>
        <ActionMenu>
          {{#each this.availablePdfOptions as |option|}}
            <ActionMenuItem {{on "click" (fn this.onOptionClick option)}} {{on "click" actions.close}}>
              {{option.pdfTypeFormatted}}
            </ActionMenuItem>
          {{/each}}
        </ActionMenu>
      </dropdown.content>
    </PopoverStandardDropdown>
  </template>
}
